// Presentation helpers for shop catalogue data. Pure functions, no I/O.

import type { Product } from './types'
import { LOCALES, type Lang } from '../../config/locales'

/**
 * Formats a price as a DKK amount with the "kr" suffix. Whole amounts render
 * without decimals; fractional amounts keep two. Locale controls the
 * decimal/thousands separators (da-DK uses a comma).
 */
export function formatKr(amount: number, locale: Lang = 'en'): string {
  if (!Number.isFinite(amount)) return `${amount} kr`
  const hasFraction = Math.round(amount * 100) % 100 !== 0
  const formatted = new Intl.NumberFormat(LOCALES[locale].intl, {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount)
  return `${formatted} kr`
}

/** The image to show for a product: whichever is flagged `primary`, else the first. */
export function primaryImage(product: Product): { url: string; altText: string | null } | null {
  return product.images.find((i) => i.primary) ?? product.images[0] ?? null
}

/** Product descriptions may carry HTML; this flattens them to one line of text. */
export function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

/** Product-type category slugs (docs/SHOP_FILTER_DATA_SPEC.md). The API names
 *  them in Danish only, so these get a `shop.categoryNames.<slug>` translation. */
export const CATEGORY_TYPE_SLUGS = [
  'shampoo',
  'conditioner',
  'hair-mask',
  'styling',
  'oil-serum',
  'hair-colour',
  'scalp-care',
  'face-body',
  'accessories',
  'gift-ideas',
  'wellness',
] as const

/** Display name for a category: translated for known types, else as the API has it. */
export function categoryLabel(
  category: { slug: string; name: string },
  t: (key: string) => string,
): string {
  return (CATEGORY_TYPE_SLUGS as readonly string[]).includes(category.slug)
    ? t(`shop.categoryNames.${category.slug}`)
    : category.name
}

/** "19. okt. 2026, 12:15" in Danish time, whatever the shopper's clock says (BUG-430, owner mock). */
export function receiptDate(iso: string, intlLocale: string): string {
  const at = new Date(iso)
  const zone = { timeZone: 'Europe/Copenhagen' } as const
  const date = new Intl.DateTimeFormat(intlLocale, { day: 'numeric', month: 'short', year: 'numeric', ...zone }).format(at)
  const time = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...zone })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  )
  return `${date}, ${time.hour}:${time.minute}`
}

